import {
    Box,
    Circle,
    HStack,
    IconButton,
    Text,
    AspectRatio,
    VStack,
    Center,
    Button,
    Image,
    Spinner,
} from '@chakra-ui/react';
import React, { useEffect, useState, useContext, useCallback } from 'react';
import AppColors from '../../../constants/AppColors';
import {
    EditIcon,
    UploadIcon,
} from '../../../constants/IconData';
import { CloseIcon, DeleteIcon } from '@chakra-ui/icons';
import { AuthContext } from '../../../providers/AuthProvider';
import { useDropzone } from 'react-dropzone';
import { baseStyle, focusedStyle, acceptStyle, rejectStyle } from '../../upload/styles';

const LogoComponent = ({
    attribute,
    logo,
    handleLogoUpdate,
}) => {
    const authContext = useContext(AuthContext);
    const VALID_IMAGE_TYPES = ['image/jpeg', 'image/png'];
    const MAX_IMAGE_SIZE_MB = 1;
    const MAX_IMAGE_SIZE_BYTES = MAX_IMAGE_SIZE_MB * 1024 * 1024;

    const [selectedImage, setSelectedImage] = useState(null);
    const [previewURL, setPreviewURL] = useState(null);
    const [error, setError] = useState('');
    const [canEdit, setCanEdit] = useState(false);
    const [submittingAction, setSubmittingAction] = useState(null);

    useEffect(() => {
        if (selectedImage) {
            const url = URL.createObjectURL(selectedImage);
            setPreviewURL(url);
            return () => URL.revokeObjectURL(url);
        } else {
            setPreviewURL(null);
        }
    }, [selectedImage]);

    const validateImage = (file) => {
        let error_msg = "";
        if (!VALID_IMAGE_TYPES.includes(file.type)) {
            error_msg = "Please upload a valid image file (JPEG, PNG).";
        } else if (file.size > MAX_IMAGE_SIZE_BYTES) {
            error_msg = `Please upload an image under ${MAX_IMAGE_SIZE_MB} MB.`;
        }

        if (error_msg) {
            setError(error_msg);
            return false;
        }
        setError('');
        return true;
    };

    const onDrop = useCallback((acceptedFiles) => {
        if (acceptedFiles.length > 0) {
            const file = acceptedFiles[0];
            if (validateImage(file)) {
                setSelectedImage(file);
            }
        }
    }, []);

    const { getRootProps, getInputProps, isFocused, isDragAccept, isDragReject } = useDropzone({
        onDrop,
        accept: { 'image/jpeg': [], 'image/png': [] },
        multiple: false,
    });

    const style = React.useMemo(
        () => ({
            ...baseStyle,
            ...(isFocused ? focusedStyle : {}),
            ...(isDragAccept ? acceptStyle : {}),
            ...(isDragReject ? rejectStyle : {}),
            padding: '20px',
            borderRadius: '10px',
            height: '100%',
            minHeight: '200px', // ensure a minimum height
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            transition: 'border-color 0.2s ease-in-out',
            cursor: 'pointer',
        }),
        [isFocused, isDragAccept, isDragReject]
    );

    const handleImageSubmit = async () => {
        if (!selectedImage) return;

        setSubmittingAction('upload');
        await handleLogoUpdate(attribute, selectedImage);
        setSubmittingAction(null);
        closeUpdatePanels();
    };

    const handleDeleteImage = async () => {
        setSubmittingAction('delete');
        await handleLogoUpdate(attribute, null); // Assuming null deletes the logo
        setSubmittingAction(null);
        closeUpdatePanels();
    };

    const resetImageData = () => {
        setSelectedImage(null);
        setError('');
    };

    const openEditPanel = () => {
        setCanEdit(true);
    };

    const closeUpdatePanels = () => {
        resetImageData();
        setCanEdit(false);
    };

    const imageContainer = (imageSrc, isPreview = false) => (
        <AspectRatio ratio={1} w="100%" maxW="200px">
            <Image
                src={imageSrc}
                alt="Client Type Logo"
                objectFit="contain"
                boxShadow={!isPreview ? "inset 3px 3px 8px rgba(0,0,0,0.2)" : "md"}
                borderRadius="20px"
                p="10px"
                bg={!isPreview ? 'transparent' : 'gray.100'}
            />
        </AspectRatio>
    );

    const renderDropzone = () => (
        <Box p={3} bgColor={AppColors.white} borderRadius={'lg'}>
            <VStack spacing={4} align="stretch">
                <Box {...getRootProps({ style })}
                     _hover={{
                        borderColor: AppColors.secondary,
                        '& .upload-text': {
                            textDecoration: 'underline'
                        }
                     }}
                >
                    <input {...getInputProps()} />
                    {previewURL ? (
                        imageContainer(previewURL, true)
                    ) : (
                        <VStack>
                            <UploadIcon boxSize={8} color="gray.500" />
                            <Text className="upload-text" textAlign="center" color="gray.600" fontWeight="bold">
                                Drag & drop or click to upload
                            </Text>
                            <Text fontSize="sm" color="gray.400">
                                PNG, JPG up to {MAX_IMAGE_SIZE_MB}MB
                            </Text>
                        </VStack>
                    )}
                </Box>

                {error && <Text color="red.500" fontSize="sm" textAlign="center">{error}</Text>}

                {submittingAction ? (
                    <Center>
                        <Spinner />
                        <Text ml={2}>{submittingAction === 'upload' ? 'Uploading...' : 'Deleting...'}</Text>
                    </Center>
                ) : (
                    <VStack spacing={2} align="stretch">
                        {previewURL && (
                            <HStack>
                                <Button colorScheme="green" onClick={handleImageSubmit} flex="1">
                                    Submit
                                </Button>
                                <Button variant="ghost" onClick={resetImageData} flex="1">
                                    Clear
                                </Button>
                            </HStack>
                        )}
                        {logo && !previewURL && canEdit && (
                            <Button
                                colorScheme="red"
                                variant="outline"
                                leftIcon={<DeleteIcon />}
                                onClick={handleDeleteImage}
                                w="100%"
                            >
                                Delete Logo
                            </Button>
                        )}
                    </VStack>
                )}
            </VStack>
        </Box>
    );

    const renderLogo = () => (
        <Box p={3} bgColor={AppColors.white} borderRadius={'lg'}>
            <Center>
                {imageContainer(logo)}
            </Center>
        </Box>
    );

    return (
        <>
            <HStack justifyContent="space-between" alignItems="center">
                <Text color={AppColors.secondary} fontSize={'md'} fontWeight="bold">
                    {authContext?.currentUser?.user_type && (authContext?.currentUser?.user_type.toLowerCase().replace(/\b\w/g, char => char.toUpperCase()))} Logo
                </Text>
                {logo && (
                    !canEdit ? (
                        <Circle
                            size="40px"
                            bg={'#EBEBEE'}
                            onClick={openEditPanel}
                            cursor={'pointer'}
                        >
                            <EditIcon />
                        </Circle>
                    ) : (
                        <IconButton
                            isRound
                            size="sm"
                            aria-label="Close edit"
                            icon={<CloseIcon />}
                            onClick={closeUpdatePanels}
                        />
                    )
                )}
            </HStack>
            <Box mt={5}>
                {!logo || canEdit ? renderDropzone() : renderLogo()}
            </Box>
        </>
    );
};

export default LogoComponent;